/**
 * The sidebar's four sections (onboarding/navigation spec, 2026-10-20) and the
 * pure URL → section mapping the rail follows on every NavigationEnd.
 *
 * Kept free of Angular so the mapping is unit-tested route by route
 * (nav-section.spec.ts) — the service only supplies the two lookups that need
 * live data.
 */
export type NavSection = 'chats' | 'topics' | 'agents' | 'teams';

/** Display order of the section items — exactly these four. */
export const NAV_SECTIONS: readonly NavSection[] = ['chats', 'topics', 'agents', 'teams'];

/**
 * No section open: a page of the rail's top rows (Dashboard, Tasks,
 * Marketplace) closes the sections instead of keeping the last one
 * (Vincent, 2026-10-20: "when I select dashboard, it should close agents").
 */
export const NO_SECTION = 'none' as const;
export type OpenSection = NavSection | typeof NO_SECTION;

/** `section:<id>` — the nav-item id of a section entry. */
export const SECTION_ITEM_PREFIX = 'section:';

export function isNavSection(value: unknown): value is NavSection {
  return typeof value === 'string' && (NAV_SECTIONS as readonly string[]).includes(value);
}

export interface SectionUrlLookups {
  /** The folder (topic) a chat belongs to, when the rail knows it; null/undefined = no folder or unknown. */
  chatFolderId(chatId: string): string | null | undefined;
  /** True when the conversation is a team's space. */
  isTeamConversation(conversationId: string): boolean;
}

/**
 * The section a URL belongs to; NO_SECTION for a top-row page (closes the
 * sections); null when the URL belongs to none (account, local models, a plain
 * channel…) — the caller then keeps the last section.
 *
 * | Route                                                    | Section    |
 * |----------------------------------------------------------|------------|
 * | /chat, /chat/c/:id without a folder                      | chats      |
 * | /chat/topics/**, /chat/folders/**, /chat/folder/**,      | topics     |
 * |   /chat/c/:id with a folder                              |            |
 * | /agents/** and /agents?conversationId=… (an agent chat)  | agents     |
 * | /agents (Dashboard), /tasks/**, /marketplace/**, /memory  | NO_SECTION |
 * | /teams/**, /messaging?conversationId=<team conversation> | teams      |
 */
export function sectionForUrl(url: string, lookups: SectionUrlLookups): OpenSection | null {
  const [rawPath, rawQuery = ''] = (url || '').split('#')[0].split('?');
  const path = rawPath.length > 1 ? rawPath.replace(/\/+$/, '') : rawPath;
  const segments = path.split('/').filter(Boolean);
  const [root, second, third] = segments;

  switch (root) {
    case 'chat': {
      if (!second) return 'chats';
      if (second === 'topics' || second === 'folders' || second === 'folder') return 'topics';
      if (second === 'c' && third) return lookups.chatFolderId(decodeURIComponent(third)) ? 'topics' : 'chats';
      return null;
    }
    case 'agents': {
      if (second) return 'agents';
      const params = new URLSearchParams(rawQuery);
      // Bare /agents is the Dashboard; an agent chat carries its conversation.
      return params.get('conversationId') || params.get('select') ? 'agents' : NO_SECTION;
    }
    case 'tasks':
    case 'marketplace':
    case 'memory':
      return NO_SECTION;
    case 'projects':
    case 'teams':
      return 'teams';
    case 'messaging': {
      const params = new URLSearchParams(rawQuery);
      const conversationId = params.get('conversationId') || params.get('select');
      return conversationId && lookups.isTeamConversation(conversationId) ? 'teams' : null;
    }
    default:
      return null;
  }
}
